import qs from 'query-string';
import {
  performGetToBackend,
  performPostToBackend,
  performPutToBackend,
} from './backend-api';
import { useRtmStore } from '@/stores/RtmStore';
import { useScheduledEventsStore } from '@/stores/ScheduledEventsStore';
import { useCareCounselorGoalsStore } from '@/stores/CareCounselorGoalsStore';
import { dateToFormattedString, getMidnightToday } from '@/utils/date';
import {
  Chat,
  ChatTreatment,
  ChatStats,
  TypedPagedResponse,
} from '@/types/Api';
import { ScheduledEvent } from '@/types/ScheduledEvent';

const getChat = async (chatId: string | undefined) => {
  const store = useRtmStore();

  if (!chatId) return null;
  const response = await performGetToBackend(`/chat/${chatId}`);
  const chat: Chat = response.data;
  store.upsertChats([chat]);
  return chat;
};

const queryChats = async (query: any) => {
  const store = useRtmStore();
  const queryString = qs.stringify(query);
  const response = await performGetToBackend(`/chat/list/all?${queryString}`);
  const { chats, page } = response.data ?? {};
  store.upsertChats(chats);
  return { chats, page };
};

const createChats = async (
  patients: {
    clinic: string;
    patient: string | undefined;
    // attributes optional if patient is provided but provided value will override the referenced patient's value
    attributes?: {
      patient_first_name: string;
      patient_last_name: string;
      phone: string;
      dob: string;
    };
  }[],
  treatment: ChatTreatment[]
) => {
  const store = useRtmStore();
  const response = await performPostToBackend('/chat/patient/import', [
    {
      group: 'Default',
      patients: patients,
      treatment,
    },
  ]); // this is an array because the backend supports importing different treatments at once
  const errors = response.data?.errors;
  if (errors.length) {
    console.error(errors);
    const firstError = errors[0];
    throw new Error(firstError.message);
  }
  const chats: Chat[] = response.data?.chats;
  store.upsertChats(chats);
  return chats;
};

const countChats = async (
  query: any
): Promise<Partial<TypedPagedResponse<Chat>>> => {
  const queryString = qs.stringify(query);
  const response = await performGetToBackend(`/chat/count?${queryString}`);
  return response.data ?? {};
};

const searchChats = async (search: string) => {
  const response = await performGetToBackend(`/chat/search?search=${search}`);
  const { chats } = response.data ?? {};
  return { chats };
};

const getChatStats = async (query: string): Promise<ChatStats> => {
  const response = await performGetToBackend(`/chat/stats?${query}`);
  return response?.data || null; // this handles if the response is undefined on 403 errors
};

const getFirstChat = async () => {
  const store = useRtmStore();

  const response = await performGetToBackend('/chat/first');
  const chat: Chat = response.data;
  store.upsertChats([chat]);
  return chat;
};

const updateChat = async (chatId: string, data: Partial<Chat>) => {
  const store = useRtmStore();

  const response = await performPutToBackend(`/chat/${chatId}`, data);

  const chat: Chat = response.data;
  store.upsertChats([chat]);
  return chat;
};

const setChatStatus = async (chatId: string, data: any) => {
  const response = await performPutToBackend(`/chat/${chatId}/status`, data);
  // we are artificially delaying the updating of the goals b/c there are already transitioning
  // UI elements that may be happening when this is called, e.g. the closing of a modal
  setTimeout(() => {
    useCareCounselorGoalsStore().refreshGoals();
  }, 2500);

  return response.data;
};

const getTwilioToken = async () => {
  const response = await performPostToBackend('/chat/twilio/token');
  return response.data?.token;
};

const suggestReply = async (chatId: string) => {
  const response = await performPostToBackend(`/chat/${chatId}/suggest`);
  return response.data;
};

const summarizeChat = async (chatId: string) => {
  const response = await performPostToBackend(`/chat/${chatId}/summarize`);
  return response.data;
};

const answerCCMQuestions = async (chatId: string, callIds: string[]) => {
  const response = await performPostToBackend(
    `/chat/${chatId}/answerCCMQuestions`,
    { calls: callIds }
  );
  const chat: Chat = response.data;
  const store = useRtmStore();
  store.upsertChats([chat]);
  return response.data;
};

const bulkAssign = async (data: any) => {
  const response = await performPostToBackend('/chat/reassign', data);
  return response.data;
};

const snoozeChat = async (
  chat: Chat,
  snoozeUntilDate: Date | null
): Promise<ScheduledEvent> => {
  const response = await performPostToBackend(`/chat/${chat._id}/snooze`, {
    snoozeUntilDate,
    title: `Message ${chat.firstName} ${chat.lastName}`,
    body: `Chat with ${chat.firstName} ${
      chat.lastName
    } was snoozed until ${dateToFormattedString(snoozeUntilDate)}`,
    pathToResource: `/chat/${chat._id}`,
  });
  if (response.data) {
    useScheduledEventsStore().upsertEvents([response.data]);
  }

  return response.data;
};

const getUpcomingEventsForChatId = async (
  chatId: string
): Promise<ScheduledEvent[]> => {
  const today = getMidnightToday();
  const response = await performGetToBackend(
    `/chat/${chatId}/upcoming-events`,
    {
      fromTime: today.getTime(),
    }
  );
  useScheduledEventsStore().upsertEvents(response.data);
  return response.data;
};

const shareChatWithUserId = async (
  chat: string,
  shareWithUserId: string,
  details: string
) => {
  const response = await performPostToBackend(`/chat/${chat._id}/share`, {
    shareWithUserId,
    details,
    pathToResource: `/chat/${chat._id}`,
  });
};

// For care counselor goals, get the current progress, the numbers of
// "completed" chats
const getChatsCompleted = async () => {
  const response = await performGetToBackend('/chat/completed');
  return response.data;
};

export {
  queryChats,
  countChats,
  createChats,
  searchChats,
  getChatStats,
  getFirstChat,
  getChat,
  updateChat,
  setChatStatus,
  getTwilioToken,
  suggestReply,
  summarizeChat,
  answerCCMQuestions,
  bulkAssign,
  snoozeChat,
  getUpcomingEventsForChatId,
  shareChatWithUserId,
  getChatsCompleted,
};
